import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import AdminLayout from '../../components/admin/AdminLayout';
import { getCourseFaq, createCourseFaq, updateCourseFaq, deleteCourseFaq, getAdminCourse } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { ArrowLeft, Plus, Trash2, Loader2, Save, X, HelpCircle } from 'lucide-react';

interface FaqItem {
  id: number;
  question: string;
  answer: string;
  category: string;
  sort_order: number;
  is_active: number;
}

export default function CourseFaqManager(): React.JSX.Element {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [course, setCourse] = useState<any>(null);
  const [faqList, setFaqList] = useState<FaqItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FaqItem | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    question: '',
    answer: '',
    category: '',
    sort_order: 0,
  });

  useEffect(() => {
    loadData();
  }, [courseId]);

  const loadData = async () => {
    if (!courseId) return;
    setLoading(true);
    try {
      const [courseRes, faqRes] = await Promise.all([
        getAdminCourse(Number(courseId)),
        getCourseFaq(Number(courseId)),
      ]);
      setCourse(courseRes.course);
      setFaqList(faqRes.faq || []);
    } catch (err: any) {
      toast.show(err.message || 'Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingFaq(null);
    setForm({ question: '', answer: '', category: '', sort_order: faqList.length });
    setShowForm(true);
  };

  const openEdit = (item: FaqItem) => {
    setEditingFaq(item);
    setForm({
      question: item.question,
      answer: item.answer,
      category: item.category || '',
      sort_order: item.sort_order,
    });
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!form.question.trim() || !form.answer.trim()) {
      toast.show('Question and answer are required', 'error');
      return;
    }
    setSaving(true);
    try {
      if (editingFaq) {
        await updateCourseFaq(Number(courseId), editingFaq.id, form);
        toast.show('FAQ updated', 'success');
      } else {
        await createCourseFaq(Number(courseId), form);
        toast.show('FAQ created', 'success');
      }
      setShowForm(false);
      loadData();
    } catch (err: any) {
      toast.show(err.message || 'Failed to save FAQ', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: FaqItem) => {
    if (!confirm(`Delete this FAQ?`)) return;
    try {
      await deleteCourseFaq(Number(courseId), item.id);
      setFaqList((prev) => prev.filter((f) => f.id !== item.id));
      toast.show('FAQ deleted', 'success');
    } catch (err: any) {
      toast.show(err.message || 'Failed to delete', 'error');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/admin/courses')}
            className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Course FAQ</h1>
            <p className="text-sm text-slate-500">{course?.title}</p>
          </div>
        </div>

        <div className="flex justify-between items-center mb-4">
          <p className="text-sm text-slate-500">{faqList.length} question{faqList.length !== 1 ? 's' : ''}</p>
          <Button size="sm" onClick={openCreate} leftIcon={<Plus className="w-4 h-4" />}>
            Add Question
          </Button>
        </div>

        {showForm && (
          <Card className="mb-6">
            <h3 className="font-semibold text-slate-900 mb-4">
              {editingFaq ? 'Edit FAQ' : 'New FAQ'}
            </h3>
            <div className="space-y-3">
              <Input
                label="Question"
                value={form.question}
                onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))}
              />
              <Input
                label="Answer"
                multiline
                value={form.answer}
                onChange={(e) => setForm((f) => ({ ...f, answer: e.target.value }))}
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Category"
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                />
                <Input
                  label="Sort Order"
                  type="number"
                  value={String(form.sort_order)}
                  onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) }))}
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button size="sm" onClick={handleSubmit} isLoading={saving} leftIcon={<Save className="w-4 h-4" />}>
                  Save
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setShowForm(false)} leftIcon={<X className="w-4 h-4" />}>
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        )}

        <div className="space-y-3">
          {faqList.length === 0 ? (
            <Card className="text-center py-12 text-slate-500">
              <HelpCircle className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p>No FAQ items yet. Add your first question above.</p>
            </Card>
          ) : (
            faqList.map((item) => (
              <Card key={item.id} className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900">{item.question}</h3>
                  <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{item.answer}</p>
                  {item.category && (
                    <span className="text-xs text-slate-400 mt-2 inline-block">{item.category}</span>
                  )}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => openEdit(item)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-amber-500 hover:bg-amber-50 transition-colors"
                  >
                    <Save className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(item)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
